/**
 * Zahlungs-Modal nach dem kostenlosen Test (DEV-1173).
 *
 * Ist der 14-Tage-Test vorbei und gibt es kein Abo, ruhen alle Kampagnen (Lizenzsperre in
 * der API, licenses/trial.service.ts). Dieses Modal liegt dann über jeder Seite und lässt
 * sich nicht wegklicken: Admin und Owner wählen die Zahlungsperiode und kaufen direkt im
 * eingebetteten Stripe-Checkout, alle anderen sehen den Hinweis an ihren Admin.
 *
 * Zustand kommt aus GET /payments/trial (beim Start, alle 5 Minuten und bei Fokus). Nach
 * dem Kauf (`?checkout=success`) wird kurz öfter gefragt, bis der Webhook die Sperre löst.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import config from '../../config';
import { API_V1 } from '../../API/APIs/APIv1';
import { dc } from '../ai-chat/design-tokens';
import { euro, monthlyTotal, PERIOD_META, type BillingPeriod } from '../ai-chat/components/license-pricing';

interface TrialStatus {
  trial: {
    endsAt: string;
    expired: boolean;
    products: Array<{ product: string; quantity: number }>;
  } | null;
  locked: boolean;
  hasSubscription: boolean;
  /** Gesperrt, oder Test vorbei ohne Abo und ohne gültigen Seat. */
  paymentRequired: boolean;
  canPurchase: boolean;
}

const POLL_MS = 5 * 60_000;
const AFTER_CHECKOUT_POLL_MS = 3_000;
const AFTER_CHECKOUT_MAX_MS = 90_000;

const PaymentRequiredModal: React.FC = () => {
  const { classes, cx } = useStyles();
  const { i18n } = useTranslation();
  const isDe = i18n.language === 'de';
  const [status, setStatus] = useState<TrialStatus | null>(null);
  const [period, setPeriod] = useState<BillingPeriod>('monthly');
  const [starting, setStarting] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const checkoutContainerRef = useRef<HTMLDivElement>(null);
  const embeddedCheckoutRef = useRef<{ destroy: () => void } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await API_V1.get('/payments/trial');
      setStatus(res.data);
    } catch {
      // Kein Status (z. B. abgemeldet) → kein Modal; der nächste Abruf versucht es wieder
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(load, POLL_MS);
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    // Zurück vom Checkout: der Webhook löst die Sperre in wenigen Sekunden
    let fast: ReturnType<typeof setInterval> | undefined;
    if (new URLSearchParams(window.location.search).get('checkout') === 'success') {
      const until = Date.now() + AFTER_CHECKOUT_MAX_MS;
      fast = setInterval(() => {
        if (Date.now() > until) clearInterval(fast);
        else void load();
      }, AFTER_CHECKOUT_POLL_MS);
    }
    return () => {
      clearInterval(timer);
      if (fast) clearInterval(fast);
      window.removeEventListener('focus', onFocus);
      embeddedCheckoutRef.current?.destroy();
      embeddedCheckoutRef.current = null;
    };
  }, [load]);

  const required = Boolean(status?.paymentRequired);
  const products = status?.trial?.products?.length
    ? status.trial.products
    : [{ product: 'saia_recruiting', quantity: 1 }];
  const monthlyBase = monthlyTotal(products);
  const meta = PERIOD_META.find((m) => m.key === period)!;
  const totalPerPeriod = monthlyBase * meta.months * (1 - meta.discount);

  // Sperre gelöst (Kauf durch): Checkout schließen
  useEffect(() => {
    if (!required && checkoutOpen) {
      embeddedCheckoutRef.current?.destroy();
      embeddedCheckoutRef.current = null;
      setCheckoutOpen(false);
    }
  }, [required, checkoutOpen]);

  const closeCheckout = () => {
    embeddedCheckoutRef.current?.destroy();
    embeddedCheckoutRef.current = null;
    setCheckoutOpen(false);
  };

  const selectPeriod = (next: BillingPeriod) => {
    if (next === period) return;
    // Ein offenes Zahlungsformular gehört zur alten Periode
    if (checkoutOpen) closeCheckout();
    setPeriod(next);
  };

  const handleCheckout = async () => {
    setStarting(true);
    setError(null);
    try {
      // App läuft unter Vites base (/dashboard), ohne das Präfix matcht der Router nach dem Checkout nicht
      const base = `${window.location.origin}${import.meta.env.BASE_URL.replace(/\/$/, '')}`;
      const res = await API_V1.post('/payments/checkout-session', {
        period,
        products,
        uiMode: 'embedded',
        successUrl: `${base}/ai-chat?checkout=success`,
        cancelUrl: `${base}/ai-chat?checkout=cancelled`,
      });
      const clientSecret = res.data?.clientSecret;
      if (!clientSecret) throw new Error('no client secret');
      const stripe = await loadStripe(config.stripe_pk);
      if (!stripe) throw new Error('stripe failed to load');
      embeddedCheckoutRef.current?.destroy();
      const checkout = await stripe.initEmbeddedCheckout({ clientSecret });
      embeddedCheckoutRef.current = checkout;
      setCheckoutOpen(true);
      // Container ist erst nach dem State-Update im DOM
      requestAnimationFrame(() => {
        if (checkoutContainerRef.current) checkout.mount(checkoutContainerRef.current);
      });
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.response?.data?.error?.message ||
          (isDe ? 'Der Checkout konnte nicht gestartet werden.' : 'Could not start the checkout.')
      );
    } finally {
      setStarting(false);
    }
  };

  if (!required) return null;

  return (
    <Dialog open fullWidth maxWidth="sm" disableEscapeKeyDown onClose={() => undefined} classes={{ paper: classes.paper }}>
      <DialogContent className={classes.content}>
        <Typography className={classes.title}>
          {isDe ? 'Dein kostenloser Test ist abgelaufen' : 'Your free trial has ended'}
        </Typography>
        <Typography variant="body2" className={classes.text}>
          {isDe
            ? 'Alle Kampagnen sind pausiert. Nach dem Kauf laufen die Kampagnen jedes LinkedIn-Kontos mit Lizenz automatisch weiter, deine Daten bleiben erhalten.'
            : 'All campaigns are paused. After the purchase the campaigns of every licensed LinkedIn account continue automatically; your data is kept.'}
        </Typography>

        {!status?.canPurchase ? (
          <Typography variant="body2" className={classes.hint}>
            {isDe
              ? 'Bitte wende dich an den Admin deines Workspace, damit er die Lizenzen kauft.'
              : 'Please ask the admin of your workspace to purchase the licenses.'}
          </Typography>
        ) : (
          <>
            <Typography variant="caption" className={classes.sectionLabel}>
              {isDe ? 'Zahlungsperiode' : 'Billing period'}
            </Typography>
            <Box className={classes.periodRow}>
              {PERIOD_META.map((m) => (
                <Box
                  key={m.key}
                  className={cx(classes.periodOption, period === m.key && 'selected')}
                  onClick={() => selectPeriod(m.key)}
                >
                  <Box className={classes.periodHead}>
                    <Typography variant="body2" className={classes.periodLabel}>
                      {isDe ? m.de : m.en}
                    </Typography>
                    {m.badge && <span className={classes.periodBadge}>{m.badge}</span>}
                  </Box>
                  <Typography variant="caption" className={classes.periodPrice}>
                    {`${euro(monthlyBase * (1 - m.discount))} / ${isDe ? 'Monat' : 'month'}`}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="body2" className={classes.total}>
              {isDe
                ? `${euro(totalPerPeriod)} ${period === 'monthly' ? 'pro Monat' : period === 'quarterly' ? 'pro Quartal' : 'pro Jahr'}, inkl. 19 % MwSt.`
                : `${euro(totalPerPeriod)} ${period === 'monthly' ? 'per month' : period === 'quarterly' ? 'per quarter' : 'per year'}, incl. 19 % VAT.`}
            </Typography>

            {checkoutOpen ? (
              <Box className={classes.checkoutBox}>
                <Box className={classes.checkoutHeader}>
                  <Typography variant="caption" className={classes.sectionLabel}>
                    {isDe ? 'Zahlungsdaten (Karte oder SEPA)' : 'Payment details (card or SEPA)'}
                  </Typography>
                  <button type="button" className={classes.linkButton} onClick={closeCheckout}>
                    {isDe ? 'Zurück' : 'Back'}
                  </button>
                </Box>
                <div ref={checkoutContainerRef} />
              </Box>
            ) : (
              <button type="button" className={classes.payButton} onClick={handleCheckout} disabled={starting}>
                {starting ? (isDe ? 'Öffnet…' : 'Opening…') : isDe ? 'Jetzt Lizenz kaufen' : 'Purchase license now'}
              </button>
            )}
          </>
        )}

        {error && (
          <Typography variant="caption" className={classes.error}>
            {error}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles()(() => ({
  paper: {
    borderRadius: 20,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: '28px 28px 24px !important',
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: dc.ink,
  },
  text: {
    color: dc.muted,
    lineHeight: 1.5,
  },
  hint: {
    padding: '10px 14px',
    borderRadius: 12,
    backgroundColor: dc.warnBg,
    border: `1px solid ${dc.warnBorder}`,
    color: dc.warnText,
  },
  sectionLabel: {
    fontWeight: 600,
    color: dc.ink,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontSize: 11,
  },
  periodRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
  },
  periodOption: {
    padding: '10px 12px',
    borderRadius: 12,
    border: `1px solid ${dc.border}`,
    cursor: 'pointer',
    '&.selected': {
      borderColor: dc.primary,
      boxShadow: dc.shadowSelected,
    },
  },
  periodHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
  },
  periodLabel: {
    fontWeight: 600,
    color: dc.ink,
  },
  periodBadge: {
    fontSize: 10,
    fontWeight: 700,
    color: dc.success,
  },
  periodPrice: {
    color: dc.muted,
  },
  total: {
    fontWeight: 600,
    color: dc.ink,
  },
  checkoutBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  checkoutHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: dc.primary,
    cursor: 'pointer',
    padding: 0,
    fontSize: 13,
  },
  payButton: {
    alignSelf: 'flex-end',
    border: 'none',
    borderRadius: 12,
    padding: '10px 20px',
    background: dc.gradient,
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer',
    '&:disabled': {
      opacity: 0.6,
      cursor: 'default',
    },
  },
  error: {
    color: '#E31A1A',
  },
}));

export default PaymentRequiredModal;
